import * as React from 'react';
import {
  Box, Grid, Card, CardMedia, CardContent, Typography,
  Button, Dialog, IconButton, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SharePointService from '../services/sharepoint.service';

// Hilfsfunktion: YouTube-Link -> Embed-URL
function toEmbedUrl(youtubeUrl, { autoplay = true } = {}) {
  try {
    const u = new URL(youtubeUrl);
    const videoId = u.searchParams.get('v');
    return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1&controls=1`;
  } catch {
    return youtubeUrl;
  }
}

// Hilfsfunktion: Thumbnail aus YouTube-Link generieren
function getYoutubeThumbnail(url) {
  try {
    const u = new URL(url);
    const videoId = u.searchParams.get('v');
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  } catch {
    return "https://placehold.co/600x338?text=Video";
  }
}

export default function CommonVideos({ grid = 3 }) {
  const mdCols = Math.max(1, Math.min(6, Math.floor(grid)));
  const smCols = Math.min(2, mdCols);
  const xs = 12;
  const sm = Math.floor(12 / smCols);
  const md = Math.floor(12 / mdCols);

  const [videos, setVideos] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(false);
  const [activeUrl, setActiveUrl] = React.useState(null);

  React.useEffect(() => {
    const fetchVideos = async () => {
      try {
        const result = await SharePointService.getCommonVideos();

        const mapped = (result || [])
          .filter((item) => item.Link)
          .map((item, idx) => ({
            id: idx,
            Title: item.Title,
            Uploaded: item.Uploaded,
            Link: item.Link,
          }))
          .sort((a, b) => {
            if (!a.Uploaded) return 1;
            if (!b.Uploaded) return -1;
            const [da, ma, ya] = a.Uploaded.split('.').map(Number);
            const [db, mb, yb] = b.Uploaded.split('.').map(Number);
            return new Date(yb, mb - 1, db) - new Date(ya, ma - 1, da);
          });

        setVideos(mapped);
      } catch (err) {
        console.error("Fehler beim Laden der allgemeinen Videos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const totalPages = Math.ceil(videos.length / mdCols) || 1;
  const startIndex = page * mdCols;
  const visible = videos.slice(startIndex, startIndex + mdCols);

  const handlePrev = () => setPage(p => (p > 0 ? p - 1 : totalPages - 1));
  const handleNext = () => setPage(p => (p < totalPages - 1 ? p + 1 : 0));

  const openVideo = (url) => {
    setActiveUrl(url);
    setOpen(true);
  };

  const closeVideo = () => {
    setOpen(false);
    setActiveUrl(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : videos.length === 0 ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          Keine allgemeinen Videos vorhanden.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {visible.map((video) => (
            <Grid item key={video.id} xs={xs} sm={sm} md={md}>
              <Card
                onClick={() => openVideo(video.Link)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  boxShadow: 1,
                  '&:hover': { boxShadow: 3, cursor: 'pointer' },
                  p: 0
                }}
              >
                <CardMedia
                  component="img"
                  height="185"
                  image={getYoutubeThumbnail(video.Link)}
                  alt={video.Title}
                  sx={{
                    objectFit: "cover",
                    borderBottom: "1px solid #ddd",
                    borderRadius: "6px 6px 0 0"
                  }}
                />

                <CardContent>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 0.5,
                    }}
                  >
                    {video.Title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {video.Uploaded}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
          <Button variant="outlined" onClick={handlePrev}>Zurück</Button>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
            Seite {page + 1} von {totalPages}
          </Typography>
          <Button variant="outlined" onClick={handleNext}>Weiter</Button>
        </Box>
      )}

      {/* Video Dialog – YouTube Embed */}
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
        open={open}
        onClose={closeVideo}
        PaperProps={{ sx: { backgroundColor: 'black' } }}
      >
        <Box sx={{ p: { xs: 0, sm: 2 }, height: fullScreen ? '100vh' : 'auto' }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", bgcolor: "white", px: 1 }}>
            <IconButton onClick={closeVideo} aria-label="Schließen">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              ...(fullScreen ? { height: '100vh' } : { aspectRatio: '16 / 9' }),
              backgroundColor: 'white',
            }}
          >
            {activeUrl && (
              <iframe
                title="Video"
                src={toEmbedUrl(activeUrl, { autoplay: true })}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}